import React, { forwardRef } from 'react';
import classNames from 'classnames';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked?: boolean;
  defaultChecked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean, e: React.ChangeEvent<HTMLInputElement>) => void;
  labelRef?: React.Ref<HTMLLabelElement>;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      children,
      checked,
      defaultChecked,
      indeterminate,
      disabled,
      onChange,
      labelRef,
      id,
      ...rest
    },
    ref
  ) => {
    const inputId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked, e);
    };

    const inputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        if (node) {
          node.indeterminate = indeterminate || false;
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [indeterminate, ref]
    );

    return (
      <label
        ref={labelRef}
        htmlFor={inputId}
        className={classNames('ecme-checkbox-wrapper', { disabled }, className)}
      >
        <span className="ecme-checkbox">
          <input
            ref={inputRef}
            id={inputId}
            type="checkbox"
            className="ecme-checkbox-input"
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={handleChange}
            {...rest}
          />
          <span className="ecme-checkbox-inner">
            {indeterminate ? (
              <svg className="ecme-checkbox-icon" viewBox="0 0 12 12" fill="currentColor">
                <rect x="2" y="5" width="8" height="2" rx="1" />
              </svg>
            ) : (
              <svg className="ecme-checkbox-icon" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="2,6 5,9 10,3" />
              </svg>
            )}
          </span>
        </span>
        {children && <span className="ecme-checkbox-label">{children}</span>}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
